/**
 * The camera's follow (slice 8c), pure so a test can run it over a whole game. The scorebug
 * covers the bottom of the canvas; the camera keeps the ball (and the carrier's feet and HP bar
 * under it) inside the band it leaves uncovered:
 *
 *  - It aims to put the ball at the vertical centre of the band, not of the canvas, easing toward
 *    that by `lerp` a frame (1 under reduced motion).
 *  - It may scroll past the field's near sideline by the inset, so a play there rises above the
 *    scorebug instead of pinning the field's edge to the canvas's bottom.
 *  - Then a hard clamp: the ball's screen y stays within [margin.top, band − margin.bottom]. The
 *    ease can lag a fast throw; the clamp cannot.
 *
 * Horizontally it is the plain follow, kept on the field. All values in device pixels.
 *
 * A formation (between plays, and a snap until the ball moves) is framed whole instead
 * (`formationScroll`): all fourteen Pokémon, each with its HP bar, inside that band.
 */

export interface Point {
  x: number;
  y: number;
}

export interface CameraView {
  /** The canvas. */
  w: number;
  h: number;
  /** How much of the canvas's bottom the scorebug covers. */
  insetBottom: number;
}

/** The field's size in the world (it starts at 0, 0). */
export interface WorldSize {
  w: number;
  h: number;
}

/** Room kept on screen above and below the ball. */
export interface Margins {
  top: number;
  bottom: number;
}

/** And beside every Pokémon in a formation: half a body, or half its HP bar if that is wider. */
export interface FormationMargins extends Margins {
  side: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** One axis: where the scroll may go, or the one place it sits when the field fits the view. */
function range(world: number, view: number, before: number, after: number): [number, number] {
  if (world + before + after <= view) {
    const centred = -(view - world) / 2;
    return [centred, centred];
  }
  return [-before, world - view + after];
}

/** The next scroll (the canvas's top-left in the world), from the last one and the ball. */
export function followScroll(
  prev: Point,
  ball: Point,
  view: CameraView,
  world: WorldSize,
  margin: Margins,
  lerp: number,
): Point {
  const band = Math.max(1, view.h - view.insetBottom);
  const [x0, x1] = range(world.w, view.w, 0, 0);
  const [y0, y1] = range(world.h, band, margin.top, margin.bottom);
  const tx = clamp(ball.x - view.w / 2, x0, x1);
  const ty = clamp(ball.y - band / 2, y0, y1);
  const x = Math.round(clamp(prev.x + (tx - prev.x) * lerp, x0, x1));
  const y = Math.round(clamp(prev.y + (ty - prev.y) * lerp, y0, y1));
  // The hard clamp, on whole pixels: never under the scorebug (that wins on a tiny canvas), and
  // never off the top.
  const lo = Math.ceil(ball.y - (band - margin.bottom));
  const hi = Math.floor(ball.y - margin.top);
  return { x, y: Math.max(lo, Math.min(hi, y)) };
}

/**
 * One axis of a formation: where a window `size` long starts. Each Pokémon at `at` needs `before`
 * and `after` around it; the ball needs the same, and must stay inside. Of the starts that hold
 * the most Pokémon (all of them when they fit), the one nearest `want`. The best starts make
 * intervals whose ends are a Pokémon's edge or the ball's limit, so trying `want` and those ends
 * finds the best exactly.
 */
function frame(
  at: readonly number[],
  ball: number,
  before: number,
  after: number,
  want: number,
  size: number,
): number {
  // The ball's limits; on a window too small to hold its margins, the lower one wins.
  const lo = Math.ceil(ball + after - size);
  const hi = Math.max(lo, Math.floor(ball - before));
  const tries = [Math.round(want), ...at.flatMap((p) => [p - before, p + after - size])];
  let best = { start: lo, held: -1, off: Infinity };
  for (const t of tries) {
    const start = clamp(t, lo, hi);
    const held = at.filter((p) => p - before >= start && p + after <= start + size).length;
    const off = Math.abs(start - want);
    if (held > best.held || (held === best.held && off < best.off)) best = { start, held, off };
  }
  return best.start;
}

/**
 * The scroll for a formation (between plays, and a snap until the ball moves). Every Pokémon at
 * `units` (world px) is framed with its body above it, its feet and HP bar below it and `side`
 * beside it, inside the band above the scorebug and the canvas's width; within that, as the
 * follow would sit (the ball centred in the band), so the play starts where the follow takes over.
 * When the band is too short for the whole formation (a phone with receivers on both sidelines; a
 * desktop's wide canvas always), it keeps the ball inside and as many Pokémon as fit around it,
 * giving up the ones farthest out. Sideways it stays on the field, as the follow does.
 */
export function formationScroll(
  ball: Point,
  units: readonly Point[],
  view: CameraView,
  world: WorldSize,
  margin: FormationMargins,
): Point {
  const band = Math.max(1, view.h - view.insetBottom);
  const [x0, x1] = range(world.w, view.w, 0, 0);
  const [y0, y1] = range(world.h, band, margin.top, margin.bottom);
  const x = frame(
    units.map((u) => u.x),
    ball.x,
    margin.side,
    margin.side,
    clamp(ball.x - view.w / 2, x0, x1),
    view.w,
  );
  const y = frame(
    units.map((u) => u.y),
    ball.y,
    margin.top,
    margin.bottom,
    clamp(ball.y - band / 2, y0, y1),
    band,
  );
  return { x: Math.round(clamp(x, x0, x1)), y };
}
